/* Charity auction front-end.

 * Three responsibilities, one file:
 * 1. Countdown — every [data-auction-countdown] ticks from one clock;
 * before the start date it counts to the START and the nearest
 * [data-countdown-heading] reads "Starts in:", after it "Ends in:";
 * past the end the bid controls disable and the heading reports
 * the close.
 * 2. Bid UI — the page bid zone and the max-bid modal (the shared
 * lightbox chrome's --panel variant: backdrop/ESC close, focus
 * trap via window.trapFocus, scroll lock — the conventions).
 * 3. Adapter seam — the auction APP owns bid truth (Webkul Product
 * Auction; see DR_Auction_Build_Report.md). Its widget mounts into
 * [data-auction-app] (#wk-auction). When the app's script has
 * populated that zone we unhide it and route bidding through it —
 * including its vault-card-before-first-bid gate. Until then,
 * "Bidding preview mode" (theme setting) keeps the comp's UI
 * honest: bids validate and echo into the history, clearly
 * labelled a preview, recorded nowhere.
 */

(() => {
  const MONEY = (cents) => {
    const dollars = cents / 100;
    return (
      '$' +
      dollars.toLocaleString('en-AU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: dollars % 1 ? 2 : 0,
      })
    ) + (window.DRPriceCurrency?.enabled ? ' ' + window.DRPriceCurrency.code : '');
  };

  const parseAmountToCents = (raw) => {
    if (!raw) return NaN;
    const cleaned = String(raw).replace(/[^0-9.]/g, '');
    if (!cleaned) return NaN;
    return Math.round(parseFloat(cleaned) * 100);
  };

  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

  /* ---------------- countdown ----------------*/

  class AuctionClock {
    constructor(root) {
      this.root = root;
      this.timers = Array.from(root.querySelectorAll('[data-auction-countdown]'));
      this.headings = Array.from(root.querySelectorAll('[data-countdown-heading]'));
      const first = this.timers[0];
      this.start = first && first.dataset.start ? Date.parse(first.dataset.start) : NaN;
      this.end = first && first.dataset.end ? Date.parse(first.dataset.end) : NaN;
      this.phase = null;
      if (this.timers.length && (!Number.isNaN(this.start) || !Number.isNaN(this.end))) {
        this.tick();
        this.interval = setInterval(() => this.tick(), 1000);
      }
    }

    tick() {
      const now = Date.now();
      let target;
      let phase;
      if (!Number.isNaN(this.start) && now < this.start) {
        phase = 'pre';
        target = this.start;
      } else if (!Number.isNaN(this.end) && now < this.end) {
        phase = 'live';
        target = this.end;
      } else {
        phase = 'ended';
        target = now;
      }
      if (phase !== this.phase) {
        this.phase = phase;
        const strings = window.DRAuctionStrings || {};
        const text =
          phase === 'pre'
            ? strings.startsIn || 'Starts in:'
            : phase === 'ended'
              ? strings.ended || 'This auction has ended.'
              : strings.endsIn || 'Ends in:';
        this.headings.forEach((h) => {
          h.textContent = text;
        });
        this.root.toggleAttribute('data-auction-ended', phase === 'ended');
        this.root.toggleAttribute('data-auction-upcoming', phase === 'pre');
        this.root.querySelectorAll('[data-auction-place], [data-auction-max-submit], [data-auction-modal-place]').forEach((btn) => {
          btn.disabled = phase !== 'live';
        });
        if (phase === 'ended') {
          this.root
            .querySelectorAll('[data-auction-place], [data-auction-max-open], [data-auction-max-submit], [data-auction-modal-place]')
            .forEach((btn) => {
              btn.disabled = true;
            });
          clearInterval(this.interval);
        }
      }
      let remaining = Math.max(0, target - now);
      const days = Math.floor(remaining / 86400000);
      remaining -= days * 86400000;
      const hours = Math.floor(remaining / 3600000);
      remaining -= hours * 3600000;
      const mins = Math.floor(remaining / 60000);
      remaining -= mins * 60000;
      const secs = Math.floor(remaining / 1000);
      this.timers.forEach((timer) => {
        timer.querySelector('[data-cd-days]').textContent = pad(days);
        timer.querySelector('[data-cd-hours]').textContent = pad(hours);
        timer.querySelector('[data-cd-mins]').textContent = pad(mins);
        timer.querySelector('[data-cd-secs]').textContent = pad(secs);
      });
    }
  }

  /* ---------------- bid UI ----------------*/

  class AuctionBidUI {
    constructor(root) {
      this.root = root;
      this.requiresLogin = root.hasAttribute('data-requires-login');
      this.increment = parseInt(root.dataset.increment, 10) || 25000;
      this.current = parseInt(root.dataset.current, 10) || 0;
      this.minimum = parseInt(root.querySelector('[data-auction-input]')?.dataset.min, 10) || this.current + this.increment;
      this.modal = root.querySelector('[data-auction-modal]');
      this.appZone = root.querySelector('[data-auction-app]');
      this.history = root.querySelector('[data-auction-history]');

      root.addEventListener('click', (e) => this.validateNativeDetails(e), true);
      root.addEventListener('click', (e) => this.onClick(e));
      this.modal?.addEventListener('keyup', (e) => {
        if (e.code === 'Escape') this.closeModal();
      });
      /* The input steps by the increment on arrow keys — the modal's
         "increment selection" without redrawing the comp*/
      root.querySelectorAll('[data-auction-input], [data-auction-max-input]').forEach((input) => {
        input.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          const cents = parseAmountToCents(input.value) || this.minCents();
          const next = e.key === 'ArrowUp' ? cents + this.increment : Math.max(this.minCents(), cents - this.increment);
          input.value = MONEY(next).replace('$', '$ ');
        });
      });

      this.watchApp();
    }

    minCents() {
      return this.minimum;
    }

    onClick(e) {
      if (e.isTrusted && e.target.closest('.pa-customer-details-form-close') && this.awaitingDetails) {
        this.pending = null;
        this.awaitingDetails = false;
        clearTimeout(this.submitTimeout);
        this.message('[data-auction-msg]', 'Bid cancelled. Your details have not been submitted.');
      }
      if (e.target.closest('[data-auction-terms-link]')) {
        const terms = this.root.querySelector('#auction-terms');
        if (terms) terms.open = true;
      }
      if (e.target.closest('[data-auction-max-open]')) this.openModal(e.target.closest('[data-auction-max-open]'));
      if (e.target.closest('[data-auction-modal-close]') || e.target === this.modal) this.closeModal();
      if (e.target.closest('[data-auction-place]')) this.submit('[data-auction-input]', '[data-auction-msg]', false);
      if (e.target.closest('[data-auction-max-submit]')) this.submit('[data-auction-max-input]', '[data-auction-modal-msg]', true);
      if (e.target.closest('[data-auction-modal-place]')) this.submit('[data-auction-max-input]', '[data-auction-modal-msg]', false);
      if (e.target.closest('[data-auction-history-all]')) {
        this.history?.classList.add('auction-history--expanded');
        e.target.closest('[data-auction-history-all]').hidden = true;
      }
    }

    openModal(trigger) {
      if (!this.modal) return;
      this.trigger = trigger;
      this.modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      window.trapFocus?.(this.modal, this.modal.querySelector('[data-auction-modal-close]'));
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.documentElement.style.overflow = '';
      window.removeTrapFocus?.(this.trigger || null);
    }

    message(sel, text) {
      const msg = this.root.querySelector(sel);
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = !text;
    }

    validateNativeDetails(e) {
      if (!e.target.closest('#pa_save_customer_details_auc')) return;
      const form = this.appZone?.querySelector('#pa_customer_required_details_form');
      if (!form || form.classList.contains('pa-hide')) return;
      const country = form.querySelector('#pa_customer_country_field')?.value;
      const fields = [
        ['#pa_customer_fname_field', '#pa_final_error'],
        ['#pa_customer_lname_field', '#pa_final_error'],
        ['#pa_customer_address1_field', '#pa_customer_details_address1_error_msg'],
        ['#pa_customer_city_field', '#pa_customer_details_city_error_msg'],
        ['#pa_customer_contact_number_field', '#pa_customer_details_contact_error_msg'],
      ];
      if (country === 'Australia' || country === 'AU') {
        fields.push(['#pa_customer_zip_code_field', '#pa_customer_details_zip_error_msg']);
        fields.push(['#pa_customer_province_field', '#pa_customer_details_province_error_msg']);
      }
      let firstMissing;
      for (const [fieldSelector, errorSelector] of fields) {
        const field = form.querySelector(fieldSelector);
        if (!field || String(field.value || '').trim()) continue;
        firstMissing ||= field;
        const error = form.querySelector(errorSelector);
        if (error) {
          error.textContent = 'This field is required.';
          error.closest('.pa-error-show-div')?.classList.remove('pa-hide');
        }
      }
      if (!firstMissing) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      firstMissing.focus();
    }

    submit(inputSel, msgSel, isMax) {
      const strings = window.DRAuctionStrings || {};
      if (this.root.hasAttribute('data-auction-ended') || this.root.hasAttribute('data-auction-upcoming')) return;
      if (this.pending) return;
      if (this.requiresLogin) {
        this.message(msgSel, strings.loginToBid || 'Sign in to place a bid.');
        return;
      }
      const input = this.root.querySelector(inputSel);
      const cents = parseAmountToCents(input?.value);
      if (!Number.isFinite(cents) || cents < this.minCents()) {
        this.message(msgSel, (strings.bidTooLow || 'Enter {amount} or more.').replace('%amount%', MONEY(this.minCents())));
        return;
      }
      const nativeInput = this.appZone?.querySelector(isMax ? '#proxy_bid_amount' : '#bid_amount');
      const nativeButton = this.appZone?.querySelector(isMax ? '.wk_proxy_submit' : '.wk_submit_bid');
      if (!this.appMode || !nativeInput || !nativeButton) {
        this.message(msgSel, 'Bidding is still loading. Please try again shortly.');
        return;
      }
      // Use the installed app's own handlers, authentication and server validation.
      // Never increment a bid locally or imply success before its server responds.
      this.pending = { msgSel, isMax, input, count: this.bidCount, bidAmount: cents / 100 };
      const oldSuccess = this.appZone.querySelector('#bidding-form .pa-success');
      if (oldSuccess) oldSuccess.style.display = 'none';
      this.appZone.querySelector('#pa_proxy_form .pa-success-box')?.classList.add('pa-hide');
      this.message(msgSel, 'Submitting your bid...');
      nativeInput.value = (cents / 100).toFixed(2);
      nativeInput.dispatchEvent(new Event('input', {bubbles:true}));
      nativeButton.click();
      clearTimeout(this.submitTimeout);
      this.submitTimeout = setTimeout(() => {
        if (!this.pending) return;
        this.message(msgSel, 'We could not confirm your bid. Refresh the page and check your bid status before trying again.');
        this.pending = null;
      }, 20000);
    }

    syncApp() {
      this.syncHistory();
      const detailsForm = this.appZone.querySelector('#pa_customer_required_details_form');
      const detailsVisible = detailsForm && !detailsForm.classList.contains('pa-hide') && getComputedStyle(detailsForm).display !== 'none';
      if (detailsVisible && !detailsForm.dataset.drRequiredLabels) {
        detailsForm.dataset.drRequiredLabels = 'true';
        ['#pa_customer_city_field', '#pa_customer_zip_code_field'].forEach((selector) => {
          const field = detailsForm.querySelector(selector);
          const label = field?.closest('.pa-form')?.querySelector('.pa-form-heading span');
          if (label && !label.textContent.includes('*')) label.textContent = `${label.textContent.trim()} *`;
          field?.setAttribute('aria-required', 'true');
        });
      }
      if (detailsVisible && this.pending?.isMax) {
        // Webkul currently resumes its normal-bid handler after this form is saved.
        // Never allow a first-time bidder's maximum amount to become a normal bid.
        detailsForm.classList.add('pa-hide');
        detailsForm.style.display = 'none';
        this.closeModal();
        this.message('[data-auction-msg]', 'To add your required details, place a regular bid first. You can then set a maximum bid. No maximum bid was placed.');
        this.message('[data-auction-modal-msg]', '');
        this.pending = null;
        clearTimeout(this.submitTimeout);
        return;
      }
      if (detailsVisible && this.pending && !this.pending.isMax && !this.awaitingDetails) {
        this.awaitingDetails = true;
        clearTimeout(this.submitTimeout);
        this.message(this.pending.msgSel, 'Complete the required details form to place your bid.');
      }
      const nativeForm = this.appZone.querySelector('#bidding-form');
      const minimumNode = nativeForm?.querySelector('.min_bid_amt');
      const minimum = parseAmountToCents(minimumNode?.textContent);
      if (!nativeForm || !Number.isFinite(minimum)) return;
      if (!this.appMode) {
        this.appMode = true;
        this.appZone.hidden = false;
        nativeForm.parentElement.setAttribute('data-native-auction-form', '');
        this.appZone.setAttribute('data-auction-adapted', '');
        this.root.setAttribute('data-auction-app-live', '');
        this.root.querySelectorAll('.auction-bid-note--demo').forEach((node) => node.remove());
      }
      this.minimum = minimum;
      this.root.querySelectorAll('[data-auction-input], [data-auction-max-input]').forEach((el) => {
        el.dataset.min = String(minimum);
        el.placeholder = `${MONEY(minimum)} or more`;
      });
      const countNode = this.appZone.querySelector('#show_bids');
      this.bidCount = parseInt(countNode?.textContent, 10) || 0;
      const current = parseAmountToCents(this.appZone.querySelector('#current_bid')?.textContent);
      if (this.bidCount > 0 && Number.isFinite(current)) {
        this.root.querySelectorAll('[data-auction-bid]').forEach((el) => {
          el.textContent = MONEY(current);
          el.closest('.auction-bid-line')?.removeAttribute('hidden');
        });
      }
      const countLabel = this.root.querySelector('[data-auction-count]');
      if (countLabel) { countLabel.textContent = `${this.bidCount} ${this.bidCount === 1 ? 'bid' : 'bids'}`; countLabel.hidden = !this.bidCount; }
      if (this.pending) {
        const proxySuccess = this.appZone.querySelector('#pa_proxy_form .pa-success-box:not(.pa-hide)');
        const normalSuccess = nativeForm.querySelector('.pa-success');
        if ((this.pending.isMax && proxySuccess) || (!this.pending.isMax && normalSuccess && getComputedStyle(normalSuccess).display !== 'none')) {
          const p = this.pending;
          this.pending = null;
          this.awaitingDetails = false;
          clearTimeout(this.submitTimeout);
          // Only the app's accepted-bid signal emits analytics. Capture the
          // submitted dollars, not the updated minimum or a subsequently edited input.
          try {
            window.drTrack?.('dr_bid_placed', {
              bid_amount: p.bidAmount,
              bid_type: p.isMax ? 'max' : 'place',
              product_handle: window.location.pathname.match(/\/products\/([^/]+)/)?.[1],
              auction_id: this.root.dataset.auctionAppId || this.root.dataset.productId,
              email: this.root.dataset.customerEmail || undefined,
            });
          } catch (_) { /* Tracking must never interrupt accepted-bid feedback. */ }
          p.input.value = '';
          this.message(p.msgSel, p.isMax ? 'Your maximum bid has been saved.' : 'Your bid has been recorded.');
        }
      }
    }

    historyStatus(text) {
      if (!this.history || this.historySignature || this.history.querySelector('.auction-history__amount')) return;
      if (this.history.dataset.status === text) return;
      this.history.dataset.status = text;
      const li = document.createElement('li');
      li.className = 'auction-history__row auction-history__row--empty';
      li.textContent = text;
      this.history.replaceChildren(li);
    }

    syncHistory() {
      const countNode = this.appZone.querySelector('#show_bids');
      const textCount = parseInt(countNode?.textContent, 10);
      const labelCount = parseInt(countNode?.getAttribute('data-bids-label'), 10);
      const count = Number.isFinite(textCount) ? textCount : Number.isFinite(labelCount) ? labelCount : this.historyResponseCount;
      const now = Date.now();
      if (Number.isFinite(count) && count !== this.historyCount) {
        const previousCount = this.historyCount;
        this.historyCount = count;
        this.historyAttempts = 0;
        this.historyLastRequest = 0;
        this.historyDeadline = now + 30000;
        if (previousCount !== undefined) this.historyRows = null;
        // The app only appends a history table if one does not already exist.
        // Keep the visible, confirmed rows while fetching an updated table.
        if (previousCount !== undefined) this.appZone.querySelector('#bidders_list')?.remove();
        this.startHistoryPoll();
      }
      const rows = this.historyRows || [...this.appZone.querySelectorAll('#bidders_list tbody tr')].filter(row => row.querySelectorAll('td').length >= 3);
      if (rows.length && this.history) {
        clearInterval(this.historyPoll);
        this.historyPoll = null;
        const signature = rows.map(row => row.textContent.trim()).join('|');
        if (signature !== this.historySignature) {
          this.historySignature = signature;
          this.history.replaceChildren(...rows.map((row, index) => {
            const li = document.createElement('li');
            li.className = `auction-history__row${index > 5 ? ' auction-history__row--extra' : ''}`;
            const cells = [...row.querySelectorAll('td')];
            [cells[1], cells[0], cells[2]].filter(Boolean).forEach((cell, column) => {
              const span = document.createElement('span');
              const value = cell.textContent.trim();
              span.textContent = column === 1 ? value.split(/\s+/).map(word => word[0]).slice(0,2).join('').toUpperCase() : value;
              span.className = ['auction-history__amount', 'auction-history__who', 'auction-history__when'][column];
              li.append(span);
            });
            return li;
          }));
          const all = this.root.querySelector('[data-auction-history-all]');
          if (all) all.hidden = rows.length <= 6;
        }
        return;
      }
      if (count === 0) {
        clearInterval(this.historyPoll);
        this.historyPoll = null;
        this.historyStatus(this.history?.dataset.empty || 'No bids have been placed yet.');
        return;
      }
      if (now >= this.historyDeadline) {
        clearInterval(this.historyPoll);
        this.historyPoll = null;
        this.historyStatus('Bidding history is temporarily unavailable. Please refresh to try again.');
        return;
      }
      this.historyStatus('Loading bidding history...');
      // Read history independently of the native click handler. That handler's
      // JSONP request has no timeout and can leave its spinner stuck indefinitely.
      // Fetching text never executes or races its shared show_bids callback.
      if ((count > 0 || (!Number.isFinite(count) && this.root.dataset.auctionAppId)) && !this.historyFetching && (this.historyAttempts || 0) < 3 &&
          now - (this.historyLastRequest || 0) >= 1000) this.fetchHistory(count);
    }

    async fetchHistory(count) {
      const auctionId = this.root.dataset.auctionAppId || this.appZone.querySelector('input[name="auction_id"]')?.value;
      const app = window.spObj || {};
      const shop = app.shop_name || window.Shopify?.shop;
      if (!auctionId || !shop) return;
      this.historyFetching = true;
      this.historyAttempts = (this.historyAttempts || 0) + 1;
      this.historyLastRequest = Date.now();
      try {
        const url = new URL('https://sp-auction.webkul.com/index.php');
        url.search = new URLSearchParams({
          p: 'wk_ajax_process', callback: 'show_bids', shop_name: shop,
          auction_id: auctionId, cust_id: '', page: 'products',
          timediff: String(app.timediff ?? new Date().getTimezoneOffset()),
          auction_config: encodeURIComponent(btoa(JSON.stringify(app.auction_config ?? false))),
          widget_config: String(app.widget_config ?? false),
          'active_currency[active]': window.Shopify?.currency?.active || 'AUD',
          'active_currency[rate]': String(window.Shopify?.currency?.rate || '1.0'),
          rate_from: String(app.rate_from || 0), rate_to: String(app.rate_to || 0),
          _: String(Date.now())
        });
        const response = await fetch(url, {credentials: 'omit', signal: AbortSignal.timeout(8000)});
        if (!response.ok) throw new Error('History request failed');
        const text = (await response.text()).trim();
        if (!text.startsWith('show_bids(')) throw new Error('Unexpected history response');
        const data = JSON.parse(text.slice('show_bids('.length).replace(/\);?\s*$/, ''));
        const doc = new DOMParser().parseFromString(String(data.bidders || ''), 'text/html');
        const rows = [...doc.querySelectorAll('tbody tr')].filter(row => row.querySelectorAll('td').length >= 3);
        const responseCount = Number(data.bid_count);
        if (!rows.length && responseCount !== 0) throw new Error('History rows not available');
        if (this.root.isConnected && this.historyCount === count) {
          this.historyResponseCount = Number.isFinite(responseCount) ? responseCount : rows.length;
          this.historyRows = rows;
        }
      } catch (_) {
        // The bounded poll retries without changing a confirmed bid into zero.
      } finally {
        this.historyFetching = false;
        this.historyLastRequest = Date.now();
        if (this.root.isConnected) this.syncHistory();
      }
    }

    startHistoryPoll() {
      if (this.historyPoll) return;
      this.historyPoll = setInterval(() => {
        if (!this.root.isConnected) {
          clearInterval(this.historyPoll);
          this.historyPoll = null;
          this.observer?.disconnect();
          return;
        }
        this.syncHistory();
      }, 500);
    }

    watchApp() {
      if (!this.appZone) return;
      this.historyDeadline = Date.now() + 30000;
      this.startHistoryPoll();
      this.observer = new MutationObserver(() => this.syncApp());
      this.observer.observe(this.appZone, {childList:true, subtree:true, characterData:true, attributes:true, attributeFilter:['class','style']});
      this.syncApp();
    }
  }

  const boot = () => {
    // Keep merchant HTML intact, including headings nested inside paragraphs.
    // Range fragments preserve inline formatting without rewriting product copy.
    document.querySelectorAll('[data-auction-description]').forEach((source) => {
      if (source.dataset.accordionsReady) return;
      const headings = [...source.querySelectorAll('b,strong,h2,h3')];
      const normal = (node) => node.textContent.trim().toLowerCase().replace(/^the\s+/, '');
      const design = headings.find((node) => normal(node) === 'design inspiration');
      const charity = headings.find((node) => normal(node) === 'why this charity');
      if (!design || !charity || !(design.compareDocumentPosition(charity) & 4)) return;
      const ranges = [document.createRange(), document.createRange()];
      ranges[0].setStartAfter(design);
      ranges[0].setEndBefore(charity);
      ranges[1].setStartAfter(charity);
      ranges[1].setEnd(source, source.childNodes.length);
      const wrapper = document.createElement('div');
      wrapper.className = 'pdp__accordions auction-story-accordions';
      ['Design Inspiration', 'Why This Charity'].forEach((label, index) => {
        const details = document.createElement('details');
        details.className = 'pdp-acc';
        const summary = document.createElement('summary');
        summary.className = 'pdp-acc__summary';
        summary.textContent = label;
        const icon = document.createElement('span');
        icon.className = 'pdp-acc__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '+';
        summary.append(icon);
        const body = document.createElement('div');
        body.className = 'pdp-acc__body rte';
        body.append(ranges[index].cloneContents());
        body.querySelectorAll('p,span,b,strong').forEach((node) => {
          if (!node.textContent.trim() && !node.querySelector('img')) node.replaceWith(document.createTextNode(node.textContent));
        });
        while (body.firstChild && (body.firstChild.nodeName === 'BR' || !body.firstChild.textContent.trim())) body.firstChild.remove();
        const panel = document.createElement('div');
        panel.className = 'pdp-acc__panel';
        panel.append(body);
        details.append(summary, panel);
        wrapper.append(details);
      });
      source.dataset.accordionsReady = 'true';
      source.replaceWith(wrapper);
    });
    document.querySelectorAll('[data-auction-root]').forEach((root) => {
      if (root.auctionInit) return;
      root.auctionInit = true;
      new AuctionClock(root);
      new AuctionBidUI(root);
    });
    document.querySelectorAll('[data-auction-card-app]').forEach((zone) => {
      if (zone.auctionInit) return;
      zone.auctionInit = true;
      const card = zone.closest('[data-auction-card]');
      const update = () => {
        const count = parseInt(zone.querySelector('.wk-bid-count')?.textContent || zone.querySelector('[data-bid-count]')?.getAttribute('data-bid-count'), 10);
        const amount = parseAmountToCents(zone.querySelector('.wk-current-bid')?.textContent);
        if (count > 0 && Number.isFinite(amount)) {
          card.querySelector('[data-auction-bid]').textContent = MONEY(amount);
          card.querySelector('.card-auction__bid').hidden = false;
        }
      };
      new MutationObserver(update).observe(zone, {subtree:true, childList:true, characterData:true, attributes:true});
      update();
    });
    if (document.querySelector('[data-auction-card]') && !window.drAuctionCardRefresh) {
      // Read the app's public category data directly. Its category widget can
      // fail during jQuery/noConflict startup, and Shopify metafields are not
      // live bid data. Never execute the returned widget scripts or styles.
      let pending = false;
      const refresh = async () => {
        if (pending || document.hidden) return;
        const cards = [...document.querySelectorAll('[data-auction-card]')];
        const ids = [...new Set(cards.map(card => card.dataset.productId).filter(id => /^\d+$/.test(id)))];
        if (!ids.length || !window.Shopify?.shop) return;
        pending = true;
        try {
          const url = new URL('https://sp-auction.webkul.com/index.php');
          url.search = new URLSearchParams({
            p: 'wk_ajax_process', callback: 'show_auction_on_category',
            shop_name: window.Shopify.shop, products: ids.join(',') + ',',
            cust_id: '', timediff: String(new Date().getTimezoneOffset()),
            general_config: btoa('false'), auction_config: btoa('false'),
            widget_config: 'false', theme_colors: btoa('false'), _: String(Date.now()),
          });
          const response = await fetch(url, {credentials: 'omit', signal: AbortSignal.timeout(10000)});
          if (!response.ok) return;
          const text = (await response.text()).trim();
          const prefix = 'show_auction_on_category(';
          if (!text.startsWith(prefix)) return;
          const payload = JSON.parse(text.slice(prefix.length).replace(/\);?\s*$/, ''));
          cards.forEach(card => {
            const html = payload[card.dataset.productId]?.template;
            if (typeof html !== 'string') return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const count = parseInt(doc.querySelector('.wk-bid-count')?.textContent || '0', 10);
            const amount = parseAmountToCents(doc.querySelector('.wk-current-bid')?.textContent);
            if (!Number.isFinite(count) || !Number.isFinite(amount)) return;
            const line = card.querySelector('.card-auction__bid');
            const value = card.querySelector('[data-auction-bid]');
            if (!line || !value) return;
            value.textContent = MONEY(amount);
            line.hidden = count < 1 || amount <= 0;
          });
        } catch (_) {
          // Keep the last confirmed value if the app is temporarily unavailable.
        } finally {
          pending = false;
        }
      };
      window.drAuctionCardRefresh = refresh;
      refresh();
      window.setInterval(refresh, 60000);
      document.addEventListener('visibilitychange', refresh);
      window.addEventListener('focus', refresh);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
